import React, { useState, Suspense } from 'react';

import {
    BrowserRouter as Router,
    Switch,
    Route,
    Redirect,
} from "react-router-dom";

import './index.css';

// ─── Always-needed (keep static) ────────────────────────────────────────────
import Sidebar from 'components/sidebar/Sidebar';
import { COLLAPSED_WIDTH } from 'components/sidebar/Sidebar';
import BottomBar from 'components/bottom-bar/BottomBar';
import Unverified from 'components/modals/Unverified';
import ErrorBoundary from 'components/error-boundary/ErrorBoundary';
import LocationProvider from 'providers/location/LocationProvider';
import PrivateRoute from 'components/private-route/PrivateRoute';
import UnauthRoute from 'components/unauth-route/UnauthRoute';
import VerifiedRoute from "components/private-route/VerifiedRoute";
import TieredRoute from 'components/private-route/TieredRoute';
import NavigationAnalyticsTracker from 'providers/firebase/NavigationAnalyticsTracker';
import UserProvider from 'providers/user/UserProvider';
import LoadingScreen from 'components/shared/LoadingScreen';
import SessionGuardProvider from 'components/shared/SessionGuardProvider';
import { getAuth, signOut } from 'firebase/auth';

import CssBaseline from '@material-ui/core/CssBaseline';
import MuiThemeProvider from '@material-ui/styles/ThemeProvider';
import theme from './Theme.js';

import GoogleFontLoader from 'react-google-font-loader';
import Snackbar from "@material-ui/core/Snackbar/Snackbar";
import Alert from "@material-ui/lab/Alert/Alert";

import { ToastProvider } from 'components/shared/ToastNotification';

import {
    isBrowser,
    isMobileOnly
} from "react-device-detect";

// ─── Lazy-loaded page components (code-split into separate chunks) ──────────
const SignInContainer = React.lazy(() => import('components/sign-in/SignInContainer'));
const SignUpContainer = React.lazy(() => import('components/sign-up/SignUpContainer'));
const EmailVerificationPending = React.lazy(() => import('components/sign-up/EmailVerificationPending'));
const ForgotPasswordContainer = React.lazy(() => import('components/forgot-password/ForgotPasswordContainer'));
const ProfileContainer = React.lazy(() => import('components/profile/ProfileContainer'));
const CreatePostContainer = React.lazy(() => import('components/create-post/CreatePostContainer'));
const MatrixFeed = React.lazy(() => import('components/feed/MatrixFeed'));
const Legal = React.lazy(() => import('components/legal/Legal'));
const TermsOfService = React.lazy(() => import('components/legal/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('components/legal/PrivacyPolicy'));
const RefundPolicy = React.lazy(() => import('components/legal/RefundPolicy'));
const CancellationPolicy = React.lazy(() => import('components/legal/CancellationPolicy'));
const StorePage = React.lazy(() => import('components/pricing/StorePage'));
const CreateMediaForm = React.lazy(() => import('components/media/CreateMediaForm'));
const CreateEventForm = React.lazy(() => import('components/events/CreateEventForm'));
const EventDetailPage = React.lazy(() => import('components/events/EventDetailPage'));
const LiveEventsPage = React.lazy(() => import('components/events/LiveEventsPage'));
const MyTickets = React.lazy(() => import('components/events/MyTickets'));
const TicketDashboard = React.lazy(() => import('components/events/TicketDashboard'));
const AutoClipDashboard = React.lazy(() => import('components/auto-clip/AutoClipDashboard'));

const SponsorshipHub = React.lazy(() => import('components/sponsorships/SponsorshipHub'));
const CreateSponsorshipForm = React.lazy(() => import('components/sponsorships/CreateSponsorshipForm'));
const OAuthCallbackPage = React.lazy(() => import('components/auto-clip/OAuthCallbackPage'));
const GoogleCalendarCallback = React.lazy(() => import('components/auto-clip/GoogleCalendarCallback'));
const LinkedInAuthCallback = React.lazy(() => import('components/sign-in/LinkedInAuthCallback'));
const NILMarketplace = React.lazy(() => import('components/nil/NILMarketplace'));
const CreateNILDealForm = React.lazy(() => import('components/nil/CreateNILDealForm'));
const HandleProfilePage = React.lazy(() => import('components/profile/HandleProfilePage'));
const TeamManagement = React.lazy(() => import('components/team/TeamManagement'));
const MyTeamPortal = React.lazy(() => import('components/team/MyTeamPortal'));
const AcceptInvitePage = React.lazy(() => import('components/team/AcceptInvitePage'));
const AgencyAthleteImport = React.lazy(() => import('components/team/AgencyAthleteImport'));
const ConnectionsHub = React.lazy(() => import('components/connections/ConnectionsHub'));
const CheckoutPage = React.lazy(() => import('components/checkout/CheckoutPage'));
const DealFinderWorkflow = React.lazy(() => import('components/deals/DealFinderWorkflow'));
const DealManagerDashboard = React.lazy(() => import('components/deals/DealManagerDashboard'));
const NILFreeTrialPage = React.lazy(() => import('components/nil/NILFreeTrialPage'));
const MyPromotions = React.lazy(() => import('components/explore/MyPromotions'));
const PhotoBackground = React.lazy(() => import('components/photo-background/PhotoBackground'));

/* ─── Apex ─── */
const ProductMarketplace = React.lazy(() => import('components/marketplace/ProductMarketplace'));
const CreateProductForm = React.lazy(() => import('components/marketplace/CreateProductForm'));
const CompanyProductDashboard = React.lazy(() => import('components/marketplace/CompanyProductDashboard'));

/* ─── Commerce Hub ─── */
const CommerceHub = React.lazy(() => import('components/commerce/CommerceHub'));

/* ─── Digital Twin Pipeline ─── */
const ContractBuilder = React.lazy(() => import('components/contracts/ContractBuilder'));
const DigitalTwinPipeline = React.lazy(() => import('components/pipeline/DigitalTwinPipeline'));

/* ─── LinkedIn Outreach CRM ─── */
const OutreachDashboard = React.lazy(() => import('components/outreach/OutreachDashboard'));

/* ─── Blast (Cross-Platform Syndication) ─── */
const BlastDashboard = React.lazy(() => import('components/blast/BlastDashboard'));

/* ─── 2.0 Module Pages ─── */
const FusionHub = React.lazy(() => import('components/fusion/FusionHub'));
const VaultStudio = React.lazy(() => import('components/vault/VaultStudio'));
const CompanySearch = React.lazy(() => import('components/companies/CompanySearch'));
const RocketCRM = React.lazy(() => import('components/rocket/RocketCRM'));
const TrustPortal = React.lazy(() => import('components/trust/TrustPortal'));
const BeaconProviderNetwork = React.lazy(() => import('components/beacon/BeaconProviderNetwork'));
const ServiceMarketplace = React.lazy(() => import('components/marketplace/ServiceMarketplace'));

/* ─── Messaging ─── */
const MessagingInbox = React.lazy(() => import('components/messaging/MessagingInbox'));
const ChatBubble = React.lazy(() => import('components/messaging/ChatBubble'));

/* ─── Notifications & Analytics ─── */
const NotificationCenter = React.lazy(() => import('components/notifications/NotificationCenter'));
const NotificationBell = React.lazy(() => import('components/notifications/NotificationBell'));
const AnalyticsDashboard = React.lazy(() => import('components/analytics/AnalyticsDashboard'));

/* ─── Social Feed ─── */

/* ─── Calendar ─── */
const CalendarDashboard = React.lazy(() => import('components/calendar/CalendarDashboard'));

/* ─── Command Palette (Cmd+K) ─── */
const CommandPalette = React.lazy(() => import('components/shared/CommandPalette'));

/* ─── Live Stream ─── */
const LiveStreamStudio = React.lazy(() => import('components/livestream/LiveStreamStudio'));

/* ─── Mobile App Shell ─── */
const MobileAppShell = React.lazy(() => import('components/mobile/MobileAppShell'));

/* ─── Database Directories ─── */
const AthletesDirectory = React.lazy(() => import('components/athletes/AthletesDirectory'));
const AthleteProfilePage = React.lazy(() => import('components/athletes/AthleteProfilePage'));
const TeamsCollectives = React.lazy(() => import('components/teams/TeamsCollectives'));

/* ─── Unified Landing Page (replaces 6 individual module landings) ─── */
const UnifiedLanding = React.lazy(() => import('components/landing/UnifiedLanding'));
const AboutPage = React.lazy(() => import('components/about/AboutPage'));

/* ─── Admin ─── */
const AdminApprovalDashboard = React.lazy(() => import('components/admin/AdminApprovalDashboard'));
const AdminSocialSync        = React.lazy(() => import('components/admin/AdminSocialSync'));

/* ─── Pricing ─── */

// OAuth Callback Path Detection
// When mobile browsers hit /oauth/{platform}/callback, they trigger the
// isMobileOnly branch which renders MobileAppShell (landing page) instead
// of the SPA router. Detect OAuth callback paths early and render the
// proper branded callback page regardless of device type.
const OAUTH_CALLBACK_REGEX = /^\/oauth\/([a-z-]+)\/callback/;

function App() {
    const [alert, setAlert] = useState({ message: "" });
    // eslint-disable-next-line no-unused-vars
    const [open, setOpen] = React.useState(false);
    const [modalOpen, setModalOpen] = React.useState(false);
    const [job, setJob] = React.useState({});

    // ── Check if we're on an OAuth callback path ──
    const oauthMatch = window.location.pathname.match(OAUTH_CALLBACK_REGEX);
    if (oauthMatch) {
        const platform = oauthMatch[1];
        // Google Calendar uses a popup+postMessage flow — render lightweight callback
        if (platform === 'google-calendar') {
            return (
                <MuiThemeProvider theme={theme}>
                    <CssBaseline />
                    <GoogleFontLoader fonts={[
                        { font: 'Exo 2', weights: [300, 400, 500, 600, 700] },
                        { font: 'Orbitron', weights: [400, 500, 600, 700, 800, 900] },
                    ]} />
                    <Suspense fallback={<LoadingScreen />}>
                        <GoogleCalendarCallback />
                    </Suspense>
                </MuiThemeProvider>
            );
        }
        return (
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                <GoogleFontLoader fonts={[
                    { font: 'Exo 2', weights: [300, 400, 500, 600, 700] },
                    { font: 'Orbitron', weights: [400, 500, 600, 700, 800, 900] },
                ]} />
                <UserProvider>
                    <Router>
                        <Suspense fallback={<LoadingScreen />}>
                            <OAuthCallbackPage platform={platform} />
                        </Suspense>
                    </Router>
                </UserProvider>
            </MuiThemeProvider>
        );
    }

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlert({ ...alert, message: "" });
        setOpen(false);
    };

    return (
        <>
            {isBrowser && (
                <UserProvider>
                    <SessionGuardProvider onSignOut={() => signOut(getAuth())}>
                    <GoogleFontLoader fonts={[
                        {
                            font: 'Orbitron',
                            weights: [400, 500, 600, 700, 800, 900],
                        },
                        {
                            font: 'Exo 2',
                            weights: [300, 400, 500, 600, 700]
                        },
                        {
                            font: 'Outfit',
                            weights: [500, 600, 700, 800, 900]
                        },
                        {
                            font: 'Inter',
                            weights: [300, 400, 500, 600, 700]
                        }
                    ]} />
                    <MuiThemeProvider theme={theme}>
                        <Snackbar anchorOrigin={{ vertical: "top", horizontal: 'center' }} style={{ marginTop: '20px', marginLeft: `${COLLAPSED_WIDTH / 2}px` }}
                            open={alert.message.length > 0} autoHideDuration={6000} onClose={handleClose}>
                            <Alert onClose={handleClose} severity={alert.severity} style={{ fontSize: '16pt' }}>
                                {alert.message}
                            </Alert>
                        </Snackbar>
                        <CssBaseline />
                        <Router>
                            <ToastProvider>
                            <LocationProvider>
                            <NavigationAnalyticsTracker />
                            <Suspense fallback={null}>
                                <CommandPalette />
                            </Suspense>
                            <Sidebar setModalOpen={(open) => { setModalOpen(open) }} setJob={setJob} />
                            <div style={{ marginLeft: COLLAPSED_WIDTH }}>
                            <Suspense fallback={<LoadingScreen />}>
                            <Switch>
                                <UnauthRoute path="/signup">
                                    <SignUpContainer setAlert={setAlert} />
                                </UnauthRoute>
                                <UnauthRoute path="/login">
                                    <SignInContainer setAlert={setAlert} />
                                </UnauthRoute>
                                <UnauthRoute path="/forgot-password">
                                    <ForgotPasswordContainer setAlert={setAlert} />
                                </UnauthRoute>
                                <Route path="/verify-email">
                                    <EmailVerificationPending />
                                </Route>
                                <VerifiedRoute path="/postJob">
                                    <CreatePostContainer job={job} setAlert={setAlert} />
                                </VerifiedRoute>
                                <PrivateRoute path="/profile">
                                    <ErrorBoundary>
                                        <ProfileContainer setAlert={setAlert} setModalOpen={(open) => { setModalOpen(open) }} />
                                    </ErrorBoundary>
                                </PrivateRoute>
                                <Route path="/explore">
                                    <MatrixFeed />
                                </Route>
                                <Redirect from="/dashboard" to="/explore" />
                                <Redirect from="/media" to="/explore" />
                                <Redirect from="/events" to="/explore" />
                                <TieredRoute path="/post-media" featureId="post_media">
                                    <CreateMediaForm />
                                </TieredRoute>
                                <TieredRoute path="/auto-clip" featureId="auto_clip">
                                    <AutoClipDashboard />
                                </TieredRoute>
                                <Route path="/oauth/tiktok/callback">
                                    <OAuthCallbackPage platform="tiktok" />
                                </Route>
                                <Route path="/oauth/instagram/callback">
                                    <OAuthCallbackPage platform="instagram" />
                                </Route>
                                <Route path="/oauth/twitter/callback">
                                    <OAuthCallbackPage platform="twitter" />
                                </Route>
                                <Route path="/auth/linkedin/callback">
                                    <LinkedInAuthCallback />
                                </Route>
                                <Route path="/oauth/youtube/callback">
                                    <OAuthCallbackPage platform="youtube" />
                                </Route>
                                <Route path="/oauth/facebook/callback">
                                    <OAuthCallbackPage platform="facebook" />
                                </Route>
                                <Route path="/oauth/linkedin/callback">
                                    <OAuthCallbackPage platform="linkedin" />
                                </Route>
                                <Route path="/oauth/google-calendar/callback">
                                    <GoogleCalendarCallback />
                                </Route>
                                <Route path="/sponsorships">
                                    <SponsorshipHub />
                                </Route>
                                <Route path="/connections">
                                    <ConnectionsHub />
                                </Route>
                                <VerifiedRoute path="/post-sponsorship">
                                    <CreateSponsorshipForm />
                                </VerifiedRoute>
                                <PrivateRoute path="/marketplace">
                                    <ProductMarketplace />
                                </PrivateRoute>
                                <VerifiedRoute path="/list-product">
                                    <CreateProductForm />
                                </VerifiedRoute>
                                <VerifiedRoute path="/seller-dashboard">
                                    <CompanyProductDashboard />
                                </VerifiedRoute>
                                <VerifiedRoute path="/commerce-hub">
                                    <CommerceHub />
                                </VerifiedRoute>
                                <PrivateRoute path="/nil">
                                    <NILMarketplace />
                                </PrivateRoute>
                                <VerifiedRoute path="/create-nil-deal">
                                    <CreateNILDealForm />
                                </VerifiedRoute>
                                <TieredRoute path="/post-event" featureId="post_event">
                                    <CreateEventForm />
                                </TieredRoute>

                                {/* ═══ Event Ticketing ═══ */}
                                <Route path="/live-events">
                                    <LiveEventsPage />
                                </Route>
                                <Route path="/event/:eventId">
                                    <EventDetailPage />
                                </Route>
                                <PrivateRoute path="/my-tickets">
                                    <MyTickets />
                                </PrivateRoute>
                                <PrivateRoute path="/ticket-dashboard/:eventId">
                                    <TicketDashboard />
                                </PrivateRoute>

                                {/* ═══ Unified Landing Page ═══ */}
                                <Route path="/features">
                                    <UnifiedLanding />
                                </Route>
                                <Route path="/about">
                                    <AboutPage />
                                </Route>
                                <Redirect from="/fusion-landing" to="/features" />
                                <Redirect from="/digital-twin-landing" to="/features" />
                                <Redirect from="/athletes-landing" to="/features" />
                                <Redirect from="/rocket-landing" to="/features" />
                                <Redirect from="/trust-landing" to="/features" />
                                <Redirect from="/beacon-landing" to="/features" />

                                {/* ═══ 2.0 Module Routes ═══ */}
                                <PrivateRoute path="/matrix">
                                    <FusionHub />
                                </PrivateRoute>
                                <Redirect from="/fusion" to="/matrix" />
                                <PrivateRoute path="/digital-twin">
                                    <VaultStudio />
                                </PrivateRoute>
                                <PrivateRoute path="/athletes">
                                    <AthletesDirectory />
                                </PrivateRoute>
                                <PrivateRoute path="/athlete-directory">
                                    <AthletesDirectory />
                                </PrivateRoute>
                                <PrivateRoute path="/athlete/:id">
                                    <AthleteProfilePage />
                                </PrivateRoute>
                                <PrivateRoute path="/teams-collectives">
                                    <TeamsCollectives />
                                </PrivateRoute>
                                <PrivateRoute path="/companies">
                                    <CompanySearch />
                                </PrivateRoute>
                                <PrivateRoute path="/rocket">
                                    <RocketCRM />
                                </PrivateRoute>
                                <PrivateRoute path="/trust">
                                    <TrustPortal />
                                </PrivateRoute>
                                <PrivateRoute path="/beacon">
                                    <BeaconProviderNetwork />
                                </PrivateRoute>
                                <Route path="/service-marketplace">
                                    <ServiceMarketplace />
                                </Route>
                                <PrivateRoute path="/team">
                                    <TeamManagement />
                                </PrivateRoute>
                                <PrivateRoute path="/my-team">
                                    <MyTeamPortal />
                                </PrivateRoute>
                                <PrivateRoute path="/agency-import">
                                    <AgencyAthleteImport />
                                </PrivateRoute>
                                <Route path="/join">
                                    <AcceptInvitePage />
                                </Route>

                                <Route path="/terms">
                                    <TermsOfService />
                                </Route>
                                <Route path="/privacy">
                                    <PrivacyPolicy />
                                </Route>
                                <Route path="/legal">
                                    <Legal />
                                </Route>
                                <Route path="/refund-policy">
                                    <RefundPolicy />
                                </Route>
                                <Route path="/cancellation-policy">
                                    <CancellationPolicy />
                                </Route>
                                <PrivateRoute path="/checkout">
                                    <CheckoutPage />
                                </PrivateRoute>
                                <PrivateRoute path="/deal-finder">
                                    <DealFinderWorkflow />
                                </PrivateRoute>
                                <PrivateRoute path="/manage-deals">
                                    <DealManagerDashboard />
                                </PrivateRoute>
                                <Route path="/nil-free-trial">
                                    <NILFreeTrialPage />
                                </Route>
                                <PrivateRoute path="/my-promotions">
                                    <MyPromotions />
                                </PrivateRoute>
                                <PrivateRoute path="/contract-builder">
                                    <ContractBuilder />
                                </PrivateRoute>
                                <PrivateRoute path="/twin-pipeline">
                                    <DigitalTwinPipeline />
                                </PrivateRoute>
                                <Route path="/store">
                                    <StorePage />
                                </Route>
                                <Redirect from="/pricing" to="/store" />
                                <Redirect from="/founding-sponsors" to="/store" />
                                <Redirect from="/feed" to="/explore" />
                                <Redirect from="/social" to="/explore" />
                                <Route path="/live-stream">
                                    <LiveStreamStudio />
                                </Route>
                                <PrivateRoute path="/messages">
                                    <MessagingInbox />
                                </PrivateRoute>
                                <PrivateRoute path="/notifications">
                                    <NotificationCenter />
                                </PrivateRoute>
                                <PrivateRoute path="/analytics">
                                    <AnalyticsDashboard />
                                </PrivateRoute>
                                <PrivateRoute path="/calendar">
                                    <CalendarDashboard />
                                </PrivateRoute>
                                <Route path="/u/:handle">
                                    <HandleProfilePage />
                                </Route>
                                <PrivateRoute path="/outreach">
                                    <OutreachDashboard />
                                </PrivateRoute>
                                <TieredRoute path="/blast" featureId="blast">
                                    <BlastDashboard />
                                </TieredRoute>
                                <Route path="/">
                                    <PhotoBackground />
                                </Route>
                                <Route path="/admin/social-sync">
                                    <AdminSocialSync />
                                </Route>
                                <Route path="/admin">
                                    <AdminApprovalDashboard />
                                </Route>
                            </Switch>
                            </Suspense>
                            <Suspense fallback={null}>
                                <NotificationBell />
                                <ChatBubble />
                            </Suspense>
                            <BottomBar />
                            </div>
                            </LocationProvider>
                            </ToastProvider>
                        </Router>
                        <Unverified modalOpen={modalOpen} setModalOpen={(open) => { setModalOpen(open) }} />
                    </MuiThemeProvider>
                    </SessionGuardProvider>
                </UserProvider>
            )}
            {isMobileOnly && (
                <>
                    <GoogleFontLoader fonts={[
                        {
                            font: 'Orbitron',
                            weights: [400, 500, 600, 700, 800, 900],
                        },
                        {
                            font: 'Exo 2',
                            weights: [300, 400, 500, 600, 700]
                        },
                        {
                            font: 'Outfit',
                            weights: [500, 600, 700, 800, 900]
                        },
                        {
                            font: 'Inter',
                            weights: [300, 400, 500, 600, 700]
                        }
                    ]} />
                    <MuiThemeProvider theme={theme}>
                        <Suspense fallback={<LoadingScreen />}>
                            <MobileAppShell />
                        </Suspense>
                    </MuiThemeProvider>
                </>
            )}
        </>

    );
}

export default App;